import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Typography, CircularProgress } from '@/ui';
import Header from '@/components/Header';
import { BenefitsService } from '@/shared/api/services';
import { resolveCustomerId } from '@/shared/lib/customerIdResolver';
import { femaCapture } from '@/shared/fema/capture';
import { httpPost } from '@/shared/api/http';
import { supportEndpoints } from '@/shared/api/endpoints';

const BenefitsCheckPage: React.FC = () => {
  const navigate = useNavigate();
  const [headerHeight, setHeaderHeight] = useState<number>(56);
  const [isClient, setIsClient] = useState<boolean>(false);
  const [iframeSrc, setIframeSrc] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [iframeLoading, setIframeLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const onceRef = useRef<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const postMessageListenerRef = useRef<((event: MessageEvent) => void) | null>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const containerStyles = useMemo(() => ({
    position: 'fixed' as const,
    top: `${headerHeight}px`,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: '#ffffff'
  }), [headerHeight]);

  const doStartCheck = async () => {
    try {
      setLoading(true);
      setError(null);

      // Step 1: Call InBest calculate endpoint to get iframe URL
      const customerId = await resolveCustomerId();
      const resp = await BenefitsService.startCheck(customerId ?? undefined);

      if (!resp?.success) {
        throw new Error(resp?.error?.message || 'Failed to start benefits check');
      }

      const data = resp.data || {};
      const iframeURL = (data as any).iFrameURL || (data as any).iframeURL || '';
      if (!iframeURL) throw new Error('No iframe URL returned');

      // Step 2: Log external link via modern endpoint
      let finalUrl = iframeURL;
      try {
        // Call modern external link log endpoint
        const logResponse = await httpPost<any>(
          supportEndpoints.logExternalLink(),
          {
            linkAddress: iframeURL,
            linkName: 'Benefits Check'
          }
        );

        // Extract linkAddress from response if available
        const logData = logResponse?.data;
        if (logData?.linkAddress) {
          finalUrl = logData.linkAddress;
        }
      } catch (logErr: any) {
        // Non-blocking - if log fails, still use original iframe URL
        femaCapture('benefits_check_log_link_failed', logErr, {
          category: 'api',
          severity: 'warning',
          skipSanitization: false,
          user: {}
        });
      }

      // Step 3: Render iframe with final URL
      setIframeSrc(finalUrl);
      setIframeLoading(true); // Start tracking iframe load
    } catch (err: any) {
      const msg = err?.message || 'Something went wrong starting the benefits check';
      setError(msg);
      femaCapture('benefits_check_start_failed', msg, {
        category: 'ui',
        severity: 'warning',
        skipSanitization: false,
        user: {}
      });
    } finally {
      setLoading(false);
    }
  };

  const handleIframeLoad = () => {
    setIframeLoading(false);
  };

  const handleIframeError = () => {
    // Suppress iframe errors silently - they might occur during navigation/completion
    // Only show error if we haven't loaded successfully yet
    if (!iframeSrc) {
      setError('Failed to load benefits check. Please try again.');
    }
  };

  // Handle postMessage events from iframe (if InBest sends completion messages)
  useEffect(() => {
    if (!isClient) return;

    const handlePostMessage = (event: MessageEvent) => {
      // Only process messages from the iframe origin (if we can verify it)
      // Note: In production, you should validate event.origin matches expected domain
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // Handle completion messages (adjust based on actual InBest message format)
        if (data?.type === 'benefits-complete' || data?.status === 'complete' || data?.completed === true) {
          setIsCompleted(true);
          setIframeLoading(false);
          
          // Log completion (non-blocking)
          femaCapture('benefits_check_completed', { message: 'Benefits check journey completed' }, {
            category: 'ui',
            severity: 'info',
            skipSanitization: false,
            user: {}
          });
        }
      } catch (err) {
        // Ignore malformed messages
      }
    };

    window.addEventListener('message', handlePostMessage);
    postMessageListenerRef.current = handlePostMessage;

    return () => {
      if (postMessageListenerRef.current) {
        window.removeEventListener('message', postMessageListenerRef.current);
        postMessageListenerRef.current = null;
      }
    };
  }, [isClient]);

  // Suppress global error handler for 500 errors that occur after iframe loads
  // These might be callback requests from InBest that fail
  useEffect(() => {
    if (!isClient || !iframeSrc || iframeLoading) return;

    const originalUnhandledRejection = window.onunhandledrejection;

    // Only suppress unhandled promise rejections for 500 errors related to benefits callbacks
    // This happens when InBest tries to call back to our backend after completion
    window.onunhandledrejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      
      // Check if this is a 500 error from a benefits-related API call
      const isBenefits500Error = 
        reason?.response?.status === 500 &&
        (
          reason?.config?.url?.includes('benefit') ||
          reason?.config?.url?.includes('inbest') ||
          reason?.config?.url?.includes('external-link')
        );
      
      // Check error message for 500/internal server error related to benefits
      const errorMsg = typeof reason === 'string' 
        ? reason.toLowerCase() 
        : (reason?.message || '').toLowerCase();
      const isBenefitsErrorMsg = 
        (errorMsg.includes('500') || errorMsg.includes('internal server error')) &&
        (errorMsg.includes('benefit') || errorMsg.includes('inbest') || errorMsg.includes('callback'));
      
      if (isBenefits500Error || isBenefitsErrorMsg) {
        // Suppress these errors silently - they're likely callback failures after completion
        // Log for debugging but don't show to user
        console.warn('Suppressed benefits callback error (500):', {
          url: reason?.config?.url,
          status: reason?.response?.status,
          message: errorMsg
        });
        
        // Still capture via FEMA for monitoring (but don't show to user)
        femaCapture('benefits_callback_error_suppressed', reason, {
          category: 'api',
          severity: 'warning',
          skipSanitization: false,
          user: {},
          context: {
            url: reason?.config?.url,
            suppressed: true,
            reason: 'Likely callback failure after benefits check completion'
          }
        });
        
        event.preventDefault(); // Prevent unhandled rejection from showing error
        return;
      }
      
      // Let other rejections through to default handler
      if (originalUnhandledRejection) {
        originalUnhandledRejection.call(window, event);
      }
    };

    return () => {
      window.onunhandledrejection = originalUnhandledRejection;
    };
  }, [isClient, iframeSrc, iframeLoading]);

  useEffect(() => {
    if (onceRef.current) return;
    onceRef.current = true;
    void doStartCheck();
  }, []);

  return (
    <Box data-testid="pages-private-from-dashboard-benefits-check-page-container-action-1" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header data-testid="pages-private-from-dashboard-benefits-check-page-header-action" onHeightChange={setHeaderHeight} />

      <Box data-testid="pages-private-from-dashboard-benefits-check-page-container-action-2" sx={containerStyles}>
        {/* Main Content */}
        <Box data-testid="pages-private-from-dashboard-benefits-check-page-container-action-3" sx={{ flex: 1, display: 'flex', flexDirection: 'column', px: { xs: 1, sm: 2, md: 3 } }}>
          {/* Status / CTA Region */}
          <Paper data-testid="pages-private-from-dashboard-benefits-check-page-container-action-4" elevation={0} sx={{ p: 2, borderBottom: '1px solid #e0e0e0', mb: 2 }}>
            <Box data-testid="pages-private-from-dashboard-benefits-check-page-container-action-5" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography data-testid="pages-private-from-dashboard-benefits-check-page-text-benefits-check" sx={{ fontSize: '18px', fontWeight: 600, color: '#00243e' }}>
                Benefits Check
              </Typography>
            </Box>
            <Typography data-testid="pages-private-from-dashboard-benefits-check-page-text-action-1" sx={{ color: '#6b7a7b', fontSize: '14px' }}>
              We partner with trusted providers to help you find missing benefits.
            </Typography>

            {isCompleted && (
              <Box data-testid="pages-private-from-dashboard-benefits-check-page-container-action-6" sx={{ mt: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: '4px' }}>
                <Typography data-testid="pages-private-from-dashboard-benefits-check-page-text-✓-benefits-check-completed-successfully" sx={{ color: '#2e7d32', fontSize: '14px', fontWeight: 500 }}>
                  ✓ Benefits check completed successfully
                </Typography>
              </Box>
            )}

            {error && (
              <Box data-testid="pages-private-from-dashboard-benefits-check-page-container-action-7" sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography data-testid="pages-private-from-dashboard-benefits-check-page-text-action-2" sx={{ color: '#9c2b2e', fontSize: '14px', fontWeight: 500 }}>
                  {error}
                </Typography>
                <Button data-testid="pages-private-from-dashboard-benefits-check-page-btn-do-start-check"
                  variant="contained"
                  onClick={doStartCheck}
                  sx={{ textTransform: 'none', bgcolor: '#155f54', '&:hover': { bgcolor: '#0d4a42' } }}
                >
                  Try again
                </Button>
              </Box>
            )}
          </Paper>

          {/* Iframe Container with margins */}
          <Box data-testid="pages-private-from-dashboard-benefits-check-page-container-action-8" sx={{ 
            flex: 1, 
            position: 'relative', 
            mb: 2,
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #e0e0e0',
            bgcolor: '#fff'
          }}>
            {/* Initial loading indicator */}
            {loading && (
              <Box data-testid="pages-private-from-dashboard-benefits-check-page-container-action-9" sx={{ 
                position: 'absolute', 
                inset: 0, 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                bgcolor: '#f5f5f5'
              }}>
                <CircularProgress size={40} sx={{ color: '#155f54' }} />
                <Typography data-testid="pages-private-from-dashboard-benefits-check-page-text-loading-benefits-check..." sx={{ mt: 2, color: '#6b7a7b', fontSize: '14px' }}>
                  Loading benefits check...
                </Typography>
              </Box>
            )}

            {/* Iframe loading indicator */}
            {!loading && iframeLoading && (
              <Box data-testid="pages-private-from-dashboard-benefits-check-page-container-action-10" sx={{ 
                position: 'absolute', 
                inset: 0, 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                bgcolor: '#f5f5f5',
                zIndex: 1
              }}>
                <CircularProgress size={40} sx={{ color: '#155f54' }} />
                <Typography data-testid="pages-private-from-dashboard-benefits-check-page-text-loading-benefits-calculator..." sx={{ mt: 2, color: '#6b7a7b', fontSize: '14px' }}>
                  Loading benefits calculator...
                </Typography>
              </Box>
            )}

            {isClient && iframeSrc && (
              <iframe
                ref={iframeRef}
                src={iframeSrc}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                title="Benefits Check Calculator"
                allow="payment; fullscreen"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block'
                }}
              />
            )}
          </Box>

          {/* Back button at bottom */}
          <Box data-testid="pages-private-from-dashboard-benefits-check-page-container-action-11" sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            pb: 2 
          }}>
            <Button data-testid="pages-private-from-dashboard-benefits-check-page-btn-back"
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{ 
                textTransform: 'none',
                borderColor: '#155f54',
                color: '#155f54',
                minWidth: '120px',
                '&:hover': { borderColor: '#0d4a42', bgcolor: 'rgba(21, 95, 84, 0.04)' }
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BenefitsCheckPage;


